import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { videoRecorderService } from '../services/videoRecorderService';
import { VideoRecording } from '../types/videoRecorder';
import { supabase } from '../config/supabaseClient';
import { toast } from 'react-hot-toast';

interface CustomVideoPlayerProps {
  video: VideoRecording;
}

const CustomVideoPlayer: React.FC<CustomVideoPlayerProps> = ({ video }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const loadVideo = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Extract filename from storage path
        const filename = video.storage_path.split('/').pop();
        if (!filename) {
          throw new Error('Invalid video path');
        }
        
        // Create URL for public streaming endpoint
        const videoUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/videos/stream/${filename}`;
        
        if (videoRef.current) {
          videoRef.current.src = videoUrl;
        }
      } catch (err) {
        console.error('Error loading video:', err);
        setError('Failed to load video');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadVideo();
  }, [video]);
  
  if (isLoading) {
    return (
      <div className="relative bg-black aspect-video rounded-lg flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="relative bg-gray-900 aspect-video rounded-lg flex justify-center items-center">
        <div className="text-white text-center p-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-red-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative bg-black rounded-lg overflow-hidden">
      <video
        ref={videoRef}
        className="w-full aspect-video"
        controls
        autoPlay
        playsInline
      />
    </div>
  );
};

const SharedVideoWatchPage: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const [video, setVideo] = useState<VideoRecording | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSharedVideo = async () => {
      if (!videoId) {
        setError('Invalid video link');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const videoData = await videoRecorderService.getVideoById(videoId);
        
        if (!videoData) {
          setError('Video not found or access denied');
          return;
        }

        // Check if video is public or if user has access
        if (!videoData.is_public) {
          setError('This video is not publicly accessible');
          return;
        }

        setVideo(videoData);
      } catch (err) {
        console.error('Error fetching shared video:', err);
        setError('Failed to load video');
        toast.error('Failed to load video');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSharedVideo();
  }, [videoId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading video...</p>
        </div>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Unable to play video</h3>
          <p className="mt-2 text-sm text-gray-500">{error || 'The video you requested could not be found or accessed.'}</p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go back home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleDownload = async () => {
    try {
      const filename = `${(video.title || 'video').replace(/[^a-zA-Z0-9-_\. ]/g, '').trim() || 'video'}.mp4`;
      const signed = await videoRecorderService.getVideoSignedUrl(video.storage_path, 60 * 30, filename);
      let url = signed;
      if (!url) {
        // Fallback to public URL if bucket/object is public
        const pub = await videoRecorderService.getVideoPublicUrl(video.storage_path);
        if (pub) {
          url = pub + (pub.includes('?') ? '&' : '?') + `download=${encodeURIComponent(filename)}`;
        }
      }
      if (!url) {
        toast.error('No downloadable URL available');
        return;
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error('Download error:', e);
      toast.error('Failed to start download');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl">
          <div className="bg-black rounded-lg overflow-hidden shadow-xl">
            {/* Custom video player for public videos */}
            <CustomVideoPlayer video={video} />
          </div>
          
          <div className="mt-6 bg-gray-800 rounded-lg shadow-md p-6 text-white">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl font-bold mb-2">{video.title || 'Untitled Video'}</h1>
                <div className="flex items-center text-gray-300 text-sm mb-4">
                  <span>Shared on {new Date(video.created_at).toLocaleDateString()}</span>
                  <span className="mx-2">•</span>
                  <span>{video.view_count || 0} views</span>
                </div>
              </div>
              <button
                onClick={handleDownload}
                className="h-10 px-4 rounded-md bg-green-600 hover:bg-green-700 text-white shrink-0"
              >
                Download
              </button>
            </div>
            {video.description && (
              <div className="mt-4">
                <h2 className="text-lg font-semibold mb-2">Description</h2>
                <p className="text-gray-300 whitespace-pre-wrap">{video.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedVideoWatchPage;
